// Centralized URL builder for app routes
// Keeps path construction consistent and enables reuse across components

type Query = Record<string, string | number | boolean | undefined | null>
export enum Section {
    Dashboard = 'dashboard',
    Campaigns = 'campaigns',
    Audience = 'audience',
    Leads = 'leads',
    Emails = 'emails',
    Calendar = 'calendar',
    KnowledgeBase = 'knowledge-base',
    Settings = 'settings',
    Integrations = 'integrations',
    Admin = 'admin',
}

function toQueryString(query?: Query): string {
    if (!query) return ''
    const parts = Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    return parts.length ? `?${parts.join('&')}` : ''
}

export const routes = {
    company: (companyId: string) => `/company/${companyId}`,
    companyWorkspace: (companyId: string, workspaceId: string) => `/company/${companyId}/workspace/${workspaceId}`,
    settingsCompany: (companyId: string) => `/company/${companyId}/settings/company`,
    settingsAuthentication: (companyId: string) => `/company/${companyId}/settings/authentication`,
    settingsIntegrations: (companyId: string) => `/company/${companyId}/settings/integrations`,
    // Workspace-scoped helpers (explicit sections now routed directly, no generic section())
    scoped: (companyId: string, workspaceId: string) => {
        const base = `/company/${companyId}`
        const wsBase = `${base}/workspace/${workspaceId}`
        return {
            root: () => wsBase,
            dashboard: () => `${wsBase}/dashboard`,
            campaignsIndex: () => `${wsBase}/campaigns`,
            audienceIndex: () => `${wsBase}/audience`,
            // Workspace email activity (ORB-385): the operational queue/triage list of sends to people.
            emails: () => `${wsBase}/emails`,
            knowledgeBase: () => `${wsBase}/knowledge-base`,
            settingsIndex: () => `${wsBase}/settings`,
            // Generic section router used by navigation components
            section: (section: Section | string) => {
                switch (section) {
                    case Section.Dashboard:
                        return `${wsBase}/dashboard`
                    case Section.Campaigns:
                        return `${wsBase}/campaigns`
                    case Section.Audience:
                        return `${wsBase}/audience`
                    case Section.Emails:
                        return `${wsBase}/emails`
                    case Section.Leads:
                        return `${wsBase}/leads`
                    case Section.KnowledgeBase:
                        return `${wsBase}/knowledge-base`
                    case Section.Settings:
                        return `${wsBase}/settings`
                    case Section.Integrations:
                        return `${base}/settings/integrations`
                    case Section.Admin:
                        return `${base}/settings/company`
                    default:
                        return `${wsBase}/dashboard`
                }
            },
            campaign: (campaignId: string, query?: Query) => `${wsBase}/campaign/${campaignId}${toQueryString(query)}`,
            campaignList: (campaignId: string, listId: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/campaign-list/${listId}${toQueryString(query)}`,
            web: (campaignId: string, webId: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/web/${webId}${toQueryString(query)}`,
            // Blast detail (ORB-384): the deep-linkable Broadcasts detail for one blast under a web.
            webBroadcast: (campaignId: string, webId: string, blastId: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/web/${webId}/broadcast/${blastId}${toQueryString(query)}`,
            // Meeting detail (ORB-436): the deep-linkable full-page recording + transcript view for one meeting under a web.
            webMeeting: (campaignId: string, webId: string, touchPointId: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/web/${webId}/meeting/${touchPointId}${toQueryString(query)}`,
            // Campaign-scoped meeting detail (ORB-436). Campaign tabs are query-based, so the open meeting
            // rides on ?meeting= alongside ?tab=meetings (mirrors the web path route above).
            campaignMeeting: (campaignId: string, touchPointId: string) =>
                `${wsBase}/campaign/${campaignId}?tab=meetings&meeting=${encodeURIComponent(touchPointId)}`,
            // Contact detail (ORB-383). Anchored on an owned record: lead | audience (FileImportRow).
            webContact: (campaignId: string, webId: string, anchorType: 'lead' | 'audience', anchorId: string, tab?: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/web/${webId}/contact/${anchorType}/${anchorId}${tab ? `/${tab}` : ''}${toQueryString(query)}`,
            campaignContact: (campaignId: string, anchorType: 'lead' | 'audience', anchorId: string, tab?: string, query?: Query) =>
                `${wsBase}/campaign/${campaignId}/contact/${anchorType}/${anchorId}${tab ? `/${tab}` : ''}${toQueryString(query)}`,
            // The workspace's companies list — the Companies half of the Leads page, addressable
            // so a company profile has a parent to point back at (ORB-623). Mirrors the path web
            // scope already uses.
            leadsCompanies: () => `${wsBase}/leads/companies`,
            // Targeted-company profile (ORB-623). Note the plural: `/company/:id` above is the
            // CLIENT company (the tenant), so a prospect company cannot live there. Matches the
            // existing `/leads/companies` vocabulary.
            companyProfile: (companyId: string, tab?: string, query?: Query) =>
                `${wsBase}/companies/${companyId}${tab ? `/${tab}` : ''}${toQueryString(query)}`,
            // Cross-campaign (workspace) contact view — view-only.
            // A lead anchors on its representative lead id; an audience anchor is someone the
            // workspace targeted who never engaged, and so has no lead to point at (ORB-623).
            workspaceContact: (anchorType: 'lead' | 'audience', anchorId: string, tab?: string, query?: Query) =>
                `${wsBase}/contact/${anchorType}/${anchorId}${tab ? `/${tab}` : ''}${toQueryString(query)}`,
            // (Leads tab deep-link helpers removed; construct manually: routes.scoped(...).web(campaignId, webId) + '/leads' or '/leads/companies'
            audienceDetail: (campaignId: string, fileImportId: string, query?: Query) => `${wsBase}/campaign/${campaignId}/audience/${fileImportId}${toQueryString(query)}`,
            settings: {
                authentication: () => `${base}/settings/authentication`,
                integrations: () => `${base}/settings/integrations`,
            },
        }
    },
}

export type Routes = typeof routes
export type WorkspaceScopedRoutes = ReturnType<typeof routes.scoped>
